import { childIsRequired } from '@@viewHelpers/validations/guaranteesPrevTns.js';
import { filter, length } from 'ramda';
import { useEffect } from 'react';
import { birthdateFromAge, dateEligibility } from '@@serverHelpers/index.mjs';
import { parseDate } from '@@viewHelpers/validations/index.js';
import moment from 'moment';

const useChildren = ({
  form,
  getMiddleNumber,
  getMiddleText,
  minBirthDateReducChild,
  minBirthDateHospiChild,
  maxBirthDateReducChild,
  maxBirthDateHospiChild
}) => {
  const maxGranteeAgeDecesReduc = getMiddleNumber(
    'NIVEAUXGARANTIESETTARIFS_GAR_DECES_REDUC_CHILD_AGE_MAXIMUM'
  );
  const maxGranteeAgeIncapHospiChild = getMiddleNumber(
    'NIVEAUXGARANTIESETTARIFS_GAR_INCAP_OPTION_HOSPI_CHILD_AGE_MAXIMUM'
  );

  const children = form.watch('children');

  const addChild = () => {
    form.setValue('children', [...children, { birthDate: null, editing: true, type: 'CHILD' }]);
    form.trigger();
  };

  const setEditingChild = (editing, key) => {
    form.setValue(
      'children',
      children.map((child, i) => (key === i ? { ...child, editing: editing } : child))
    );
  };

  const deleteChild = (key) => {
    form.setValue(
      'children',
      children.filter((_, i) => i !== key)
    );
    form.trigger();
  };

  const childHasBirthDate = (key) => form.watch(`children.${key}.birthDate`);

  const minChildAge = getMiddleNumber('BENEFICIAIRE_DATE_NAISSANCE_AGE_MINIMUM_CHILD');
  const maxChildAge = getMiddleNumber('BENEFICIAIRE_DATE_NAISSANCE_AGE_MAXIMUM_CHILD');
  const today = moment().endOf('day');
  const parsedEffectDate = parseDate(form.watch('offer.effectDate'));
  const effectDate = parsedEffectDate?.isValid() ? parsedEffectDate : today;
  const typeCalculAge = getMiddleText('BENEFICIAIRE_DATE_NAISSANCE_TYPE_CALCUL_AGE');

  let maxChildPickerBirthDate = today;
  if (minChildAge > -1) {
    const calculatedMax = birthdateFromAge(minChildAge, effectDate, typeCalculAge, false);
    if (calculatedMax?.isValid()) {
      maxChildPickerBirthDate = moment.min(calculatedMax, today);
    }
  }

  let minChildPickerBirthDate = null;
  if (maxChildAge > -1) {
    const calculatedMin = birthdateFromAge(maxChildAge, effectDate, typeCalculAge, true);
    if (calculatedMin?.isValid()) {
      minChildPickerBirthDate = calculatedMin;
    }
  }

  const nbMaxChild = getMiddleNumber('BENEFICIAIRE_TYPE_NB_MAX_CHILD');
  const isChildMaxCountReached = length(children) >= nbMaxChild;

  // If the option of reduc or option of hospitalisation of child is selected
  // At least one child needs an eligible birthdate
  useEffect(() => {
    const OPTION_REDUC = form.watch('guarantees.OPTION_REDUC.selected');
    const OPTION_HOSP_ENF_GAR = form.watch('guarantees.OPTION_HOSP_ENF_GAR.selected');

    if (OPTION_REDUC || OPTION_HOSP_ENF_GAR) {
      const eligibleChildrenReduc = filter(
        (child) => {
          const parsed = parseDate(child.birthDate);
          return child.type === 'CHILD' &&
            parsed.isValid() &&
            dateEligibility(parsed.toDate(), minBirthDateReducChild, maxBirthDateReducChild).label ===
              'Eligible';
        },
        children
      );

      const eligibleChildrenHospi = filter(
        (child) => {
          const parsed = parseDate(child.birthDate);
          return child.type === 'CHILD' &&
            parsed.isValid() &&
            dateEligibility(parsed.toDate(), minBirthDateHospiChild, maxBirthDateHospiChild).label ===
              'Eligible';
        },
        children
      );

      if (children.length === 0) {
        form.clearErrors('guarantees.children');
      } else if (
        (OPTION_REDUC && eligibleChildrenReduc.length > 0) ||
        (OPTION_HOSP_ENF_GAR && eligibleChildrenHospi.length > 0)
      ) {
        form.clearErrors('guarantees.children');
      } else {
        form.setError('guarantees.children', {
          type: 'custom',
          message:
            'Pour souscrire aux garanties rente éducation et/ou hospitalisation des enfants, vous devez saisir au moins un enfant avec une date de naissance éligible à la garantie sélectionnée.'
        });
      }
    } else {
      form.clearErrors('guarantees.children');
    }
  }, [
    children,
    minBirthDateReducChild,
    maxBirthDateReducChild,
    minBirthDateHospiChild,
    maxBirthDateHospiChild
  ]);

  return {
    children,
    addChild,
    setEditingChild,
    deleteChild,
    childHasBirthDate,
    childIsRequired,
    isChildMaxCountReached,
    nbMaxChild,
    maxGranteeAgeIncapHospiChild,
    maxGranteeAgeDecesReduc,
    minChildPickerBirthDate,
    maxChildPickerBirthDate
  };
};

export default useChildren;
